import { z } from 'zod'
import { asObjectType, asEnumType } from '@gqloom/zod'

// Enums
export const KnowledgeArticleStatus = {
  Draft: 'DRAFT',
  Published: 'PUBLISHED',
  Archived: 'ARCHIVED',
} as const

export const TranslationStatus = {
  Pending: 'PENDING',
  Translated: 'TRANSLATED',
  Failed: 'FAILED',
  Reviewed: 'REVIEWED',
} as const

// Type exports for enums
export type KnowledgeArticleStatusValue =
  (typeof KnowledgeArticleStatus)[keyof typeof KnowledgeArticleStatus]
export type TranslationStatusValue = (typeof TranslationStatus)[keyof typeof TranslationStatus]

// Cover image position schema
export const coverImagePositionSchema = z
  .object({
    x: z.number().min(0).max(100),
    y: z.number().min(0).max(100),
  })
  .register(asObjectType, {
    name: 'CoverImagePosition',
    description: 'Position coordinates for cover image focal point (0-100%)',
  })

// Knowledge Article schema
export const knowledgeArticleSchema = z
  .object({
    knowledgeArticleId: z.string().uuid(),
    slug: z.string().min(1).max(255),
    title: z.string().min(1).max(255),
    subtitle: z.string().max(500).nullable().optional(),
    contentMarkdown: z.string().min(1),
    summary: z.string().max(1000).nullable().optional(),
    coverImageUrl: z.string().max(2048).nullable().optional(),
    coverImagePosition: coverImagePositionSchema.nullable().optional(),
    tierTags: z.array(z.string().max(50)).nullable().optional(),
    roleTags: z.array(z.string().max(50)).nullable().optional(),
    permissionTags: z.array(z.string().max(100)).nullable().optional(),
    category: z.string().max(100).nullable().optional(),
    tags: z.array(z.string().max(100)).nullable().optional(),
    metaTitle: z.string().max(255).nullable().optional(),
    metaDescription: z.string().max(500).nullable().optional(),
    status: z.nativeEnum(KnowledgeArticleStatus).register(asEnumType, {
      name: 'KnowledgeArticleStatus',
    }),
    publishedAt: z.date().nullable().optional(),
    version: z.number().int().default(1),
    createdBy: z.string().uuid(),
    updatedBy: z.string().uuid().nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'KnowledgeArticle',
    description: 'A knowledge base article for the support system',
  })

// Create schema - excludes system fields
export const knowledgeArticleCreateSchema = z
  .object({
    slug: z.string().min(1).max(255),
    title: z.string().min(1).max(255),
    subtitle: z.string().max(500).nullable().optional(),
    contentMarkdown: z.string().min(1),
    summary: z.string().max(1000).nullable().optional(),
    coverImageUrl: z.string().max(2048).nullable().optional(),
    coverImagePosition: coverImagePositionSchema.nullable().optional(),
    tierTags: z.array(z.string().max(50)).nullable().optional(),
    roleTags: z.array(z.string().max(50)).nullable().optional(),
    permissionTags: z.array(z.string().max(100)).nullable().optional(),
    category: z.string().max(100).nullable().optional(),
    tags: z.array(z.string().max(100)).nullable().optional(),
    metaTitle: z.string().max(255).nullable().optional(),
    metaDescription: z.string().max(500).nullable().optional(),
    status: z.nativeEnum(KnowledgeArticleStatus).optional().default(KnowledgeArticleStatus.Draft),
  })
  .register(asObjectType, {
    name: 'KnowledgeArticleCreateInput',
    description: 'Input for creating a knowledge article',
  })

// Update schema - all content fields optional
export const knowledgeArticleUpdateSchema = z
  .object({
    slug: z.string().min(1).max(255).optional(),
    title: z.string().min(1).max(255).optional(),
    subtitle: z.string().max(500).nullable().optional(),
    contentMarkdown: z.string().min(1).optional(),
    summary: z.string().max(1000).nullable().optional(),
    coverImageUrl: z.string().max(2048).nullable().optional(),
    coverImagePosition: coverImagePositionSchema.nullable().optional(),
    tierTags: z.array(z.string().max(50)).nullable().optional(),
    roleTags: z.array(z.string().max(50)).nullable().optional(),
    permissionTags: z.array(z.string().max(100)).nullable().optional(),
    category: z.string().max(100).nullable().optional(),
    tags: z.array(z.string().max(100)).nullable().optional(),
    metaTitle: z.string().max(255).nullable().optional(),
    metaDescription: z.string().max(500).nullable().optional(),
    status: z.nativeEnum(KnowledgeArticleStatus).optional(),
    publishedAt: z.date().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'KnowledgeArticleUpdateInput',
    description: 'Input for updating a knowledge article',
  })

// Filter schema for queries
export const knowledgeArticleFilterSchema = z
  .object({
    status: z.nativeEnum(KnowledgeArticleStatus).optional(),
    category: z.string().optional(),
    tags: z.array(z.string()).optional(),
    tierTags: z.array(z.string()).optional(),
    roleTags: z.array(z.string()).optional(),
    permissionTags: z.array(z.string()).optional(),
    search: z.string().optional(),
    includeDeleted: z.boolean().optional().default(false),
  })
  .register(asObjectType, {
    name: 'KnowledgeArticleFilterInput',
    description: 'Filter options for querying knowledge articles',
  })

// Knowledge Article Translation schema
export const knowledgeArticleTranslationSchema = z
  .object({
    translationId: z.string().uuid(),
    knowledgeArticleId: z.string().uuid(),
    locale: z.string().min(2).max(10),
    title: z.string().min(1).max(255),
    subtitle: z.string().max(500).nullable().optional(),
    contentMarkdown: z.string().min(1),
    summary: z.string().max(1000).nullable().optional(),
    metaTitle: z.string().max(255).nullable().optional(),
    metaDescription: z.string().max(500).nullable().optional(),
    status: z.nativeEnum(TranslationStatus).register(asEnumType, {
      name: 'TranslationStatus',
    }),
    translatedAt: z.date().nullable().optional(),
    reviewedBy: z.string().uuid().nullable().optional(),
    reviewedAt: z.date().nullable().optional(),
    sourceVersion: z.number().int(),
    needsRetranslation: z.boolean().default(false),
    createdAt: z.date(),
    updatedAt: z.date(),
  })
  .register(asObjectType, {
    name: 'KnowledgeArticleTranslation',
    description: 'A translated version of a knowledge article',
  })

// Knowledge Article Override schema (for corporate customizations)
export const knowledgeArticleOverrideSchema = z
  .object({
    knowledgeArticleOverrideId: z.string().uuid(),
    defaultArticleId: z.string().uuid(),
    title: z.string().max(255).nullable().optional(),
    subtitle: z.string().max(500).nullable().optional(),
    contentMarkdown: z.string().min(1),
    summary: z.string().max(1000).nullable().optional(),
    status: z.nativeEnum(KnowledgeArticleStatus).default(KnowledgeArticleStatus.Published),
    isCustomized: z.boolean().default(true),
    createdBy: z.string().uuid(),
    updatedBy: z.string().uuid().nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'KnowledgeArticleOverride',
    description: 'A corporate-specific customization of a knowledge article',
  })

export const knowledgeArticleOverrideCreateSchema = z
  .object({
    defaultArticleId: z.string().uuid(),
    title: z.string().max(255).nullable().optional(),
    subtitle: z.string().max(500).nullable().optional(),
    contentMarkdown: z.string().min(1),
    summary: z.string().max(1000).nullable().optional(),
  })
  .register(asObjectType, {
    name: 'KnowledgeArticleOverrideCreateInput',
    description: 'Input for creating a knowledge article override',
  })

export const knowledgeArticleOverrideUpdateSchema = z
  .object({
    title: z.string().max(255).nullable().optional(),
    subtitle: z.string().max(500).nullable().optional(),
    contentMarkdown: z.string().min(1).optional(),
    summary: z.string().max(1000).nullable().optional(),
    status: z.nativeEnum(KnowledgeArticleStatus).optional(),
  })
  .register(asObjectType, {
    name: 'KnowledgeArticleOverrideUpdateInput',
    description: 'Input for updating a knowledge article override',
  })

// Knowledge Article Category schema
export const knowledgeArticleCategorySchema = z
  .object({
    knowledgeArticleCategoryId: z.string().uuid(),
    name: z.string().min(1).max(100),
    slug: z.string().min(1).max(100),
    icon: z.string().max(50).nullable().optional(),
    color: z.string().max(7).nullable().optional(),
    sortOrder: z.number().int().default(0),
    articleCount: z.number().int().default(0),
    draftCount: z.number().int().default(0),
    createdBy: z.string().uuid(),
    updatedBy: z.string().uuid().nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'KnowledgeArticleCategory',
    description: 'A category for organizing knowledge articles',
  })

export const knowledgeArticleCategoryCreateSchema = z
  .object({
    name: z.string().min(1).max(100),
    slug: z.string().min(1).max(100).optional(),
    icon: z.string().max(50).nullable().optional(),
    color: z.string().max(7).nullable().optional(),
    sortOrder: z.number().int().optional(),
  })
  .register(asObjectType, {
    name: 'KnowledgeArticleCategoryCreateInput',
    description: 'Input for creating a knowledge article category',
  })

export const knowledgeArticleCategoryUpdateSchema = z
  .object({
    name: z.string().min(1).max(100).optional(),
    slug: z.string().min(1).max(100).optional(),
    icon: z.string().max(50).nullable().optional(),
    color: z.string().max(7).nullable().optional(),
    sortOrder: z.number().int().optional(),
  })
  .register(asObjectType, {
    name: 'KnowledgeArticleCategoryUpdateInput',
    description: 'Input for updating a knowledge article category',
  })

// Type exports
export type CoverImagePosition = z.infer<typeof coverImagePositionSchema>
export type KnowledgeArticle = z.infer<typeof knowledgeArticleSchema>
export type KnowledgeArticleCreate = z.infer<typeof knowledgeArticleCreateSchema>
export type KnowledgeArticleUpdate = z.infer<typeof knowledgeArticleUpdateSchema>
export type KnowledgeArticleFilter = z.infer<typeof knowledgeArticleFilterSchema>
export type KnowledgeArticleTranslation = z.infer<typeof knowledgeArticleTranslationSchema>
export type KnowledgeArticleOverride = z.infer<typeof knowledgeArticleOverrideSchema>
export type KnowledgeArticleOverrideCreate = z.infer<typeof knowledgeArticleOverrideCreateSchema>
export type KnowledgeArticleOverrideUpdate = z.infer<typeof knowledgeArticleOverrideUpdateSchema>
export type KnowledgeArticleCategory = z.infer<typeof knowledgeArticleCategorySchema>
export type KnowledgeArticleCategoryCreate = z.infer<typeof knowledgeArticleCategoryCreateSchema>
export type KnowledgeArticleCategoryUpdate = z.infer<typeof knowledgeArticleCategoryUpdateSchema>
